import { useState, type CSSProperties } from 'react';
import { useTheme } from '../../context/ThemeContext.jsx';

export default function Sidebar({
  activeCollection, showFavorites, activeTag,
  collections, tags,
  onNavigate,
  onCreateCollection, onUpdateCollection, onRemoveCollection,
  onActivateCollection,
  onCreateTag, onRemoveTag,
}) {
  const { theme } = useTheme();

  const [newCollectionName, setNewCollectionName] = useState('');
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [editingCollectionId, setEditingCollectionId] = useState(null);
  const [editingCollectionName, setEditingCollectionName] = useState('');
  const [collectionMenuId, setCollectionMenuId] = useState(null);
  const [expandedCollections, setExpandedCollections] = useState(new Set());
  const [newSubCollectionParent, setNewSubCollectionParent] = useState(null);

  const sidebarBtnStyle = (isActive: boolean): CSSProperties => ({
    width: '100%', padding: '7px 10px 7px 8px',
    background: 'none',
    border: 'none', borderLeft: `2px solid ${isActive ? theme.accent : 'transparent'}`,
    borderRadius: 0,
    color: isActive ? theme.text : theme.textTertiary,
    fontSize: 13, fontWeight: 400, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 2, textAlign: 'left', fontFamily: 'inherit',
  });

  const rootCollections = collections.filter(c => !c.parent_id || c.parent_id === null);
  const childCollections = (parentId) => collections.filter(c => c.parent_id === parentId);

  const renderCollection = (c, depth = 0) => {
    const children = childCollections(c.id);
    const isExpanded = expandedCollections.has(c.id);
    const hasChildren = children.length > 0;

    return (
      <div key={c.id || c.name} style={{ paddingLeft: depth > 0 ? 12 : 0 }}>
        <div
          style={{ position: 'relative' }}
          onMouseEnter={e => { const dots = e.currentTarget.querySelector<HTMLElement>('.col-dots'); if (dots) dots.style.opacity = '1'; }}
          onMouseLeave={e => { const dots = e.currentTarget.querySelector<HTMLElement>('.col-dots'); if (dots) dots.style.opacity = '0'; setCollectionMenuId(null); }}
        >
          {editingCollectionId === c.id ? (
            <input
              autoFocus
              value={editingCollectionName}
              onChange={e => setEditingCollectionName(e.target.value)}
              onKeyDown={async e => {
                if (e.key === 'Enter' && editingCollectionName.trim()) {
                  await onUpdateCollection(c.id, { name: editingCollectionName.trim() });
                  setEditingCollectionId(null);
                }
                if (e.key === 'Escape') setEditingCollectionId(null);
              }}
              onBlur={() => setEditingCollectionId(null)}
              style={{
                width: '100%', padding: '6px 8px',
                background: theme.bgInput, border: `1px solid ${theme.accentBorder}`,
                borderRadius: 0, color: theme.text, fontSize: 12, outline: 'none',
                fontFamily: 'inherit', marginBottom: 2,
              }}
            />
          ) : (
            <button
              onClick={() => onNavigate('collection', c.name, c.id)}
              style={sidebarBtnStyle(activeCollection === c.name && !showFavorites && !activeTag)}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {hasChildren && (
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      setExpandedCollections(prev => {
                        const next = new Set(prev);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        return next;
                      });
                    }}
                    style={{ fontSize: 8, color: theme.textGhost, cursor: 'pointer', padding: '0 2px' }}
                  >{isExpanded ? '▾' : '▸'}</span>
                )}
                <span style={{ fontSize: 13, opacity: 0.7 }}>{c.icon || '◈'}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 10, color: theme.textGhost, fontWeight: 600 }}>{c.font_count}</span>
                {c.id > 0 && (
                  <>
                    <span
                      onClick={e => { e.stopPropagation(); onActivateCollection(c.id); }}
                      style={{ fontSize: 11, color: theme.textDisabled, cursor: 'pointer', padding: '0 1px' }}
                      title="Activate all fonts"
                    >⚡</span>
                    <span
                      className="col-dots"
                      onClick={e => { e.stopPropagation(); setCollectionMenuId(collectionMenuId === c.id ? null : c.id); }}
                      style={{
                        opacity: 0, transition: 'opacity 0.15s',
                        fontSize: 14, color: theme.textFaint, cursor: 'pointer',
                        padding: '0 2px', lineHeight: 1,
                      }}
                    >...</span>
                  </>
                )}
              </span>
            </button>
          )}

          {collectionMenuId === c.id && (
            <div style={{
              position: 'absolute', right: 4, top: '100%', zIndex: 50,
              background: theme.bgElevated, border: `1px solid ${theme.borderHover}`,
              borderRadius: 0, padding: 4, minWidth: 140,
              boxShadow: theme.shadow,
            }}>
              <button
                onClick={e => { e.stopPropagation(); setEditingCollectionId(c.id); setEditingCollectionName(c.name); setCollectionMenuId(null); }}
                style={{
                  width: '100%', padding: '6px 10px', background: 'none',
                  border: 'none', borderRadius: 0, color: theme.textSecondary,
                  fontSize: 11, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.background = theme.bgHover}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >Rename</button>
              <button
                onClick={e => {
                  e.stopPropagation();
                  setNewSubCollectionParent(c.id);
                  setCollectionMenuId(null);
                }}
                style={{
                  width: '100%', padding: '6px 10px', background: 'none',
                  border: 'none', borderRadius: 0, color: theme.textSecondary,
                  fontSize: 11, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.background = theme.bgHover}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >Add Sub-Collection</button>
              <button
                onClick={async e => {
                  e.stopPropagation();
                  if (confirm(`Delete collection "${c.name}"?`)) {
                    await onRemoveCollection(c.id);
                    if (activeCollection === c.name) onNavigate('collection', 'All Fonts', 0);
                  }
                  setCollectionMenuId(null);
                }}
                style={{
                  width: '100%', padding: '6px 10px', background: 'none',
                  border: 'none', borderRadius: 0, color: theme.red,
                  fontSize: 11, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.background = theme.redBg}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >Delete</button>
            </div>
          )}
        </div>

        {newSubCollectionParent === c.id && (
          <div style={{ padding: '2px 4px 2px 16px', marginBottom: 4 }}>
            <input
              autoFocus
              placeholder="Sub-collection name..."
              value={newCollectionName}
              onChange={e => setNewCollectionName(e.target.value)}
              onKeyDown={async e => {
                if (e.key === 'Enter' && newCollectionName.trim()) {
                  await onCreateCollection({ name: newCollectionName.trim(), parent_id: c.id });
                  setNewCollectionName('');
                  setNewSubCollectionParent(null);
                  setExpandedCollections(prev => new Set([...prev, c.id]));
                }
                if (e.key === 'Escape') { setNewCollectionName(''); setNewSubCollectionParent(null); }
              }}
              onBlur={() => { setNewCollectionName(''); setNewSubCollectionParent(null); }}
              style={{
                width: '100%', padding: '6px 8px',
                background: theme.bgInput, border: `1px solid ${theme.accentBorder}`,
                borderRadius: 0, color: theme.text, fontSize: 12, outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
        )}

        {isExpanded && children.map(child => renderCollection(child, depth + 1))}
      </div>
    );
  };

  return (
    <aside style={{
      flex: '0 0 236px', minWidth: 200, borderRight: `1px solid ${theme.border}`,
      padding: '20px 0', flexShrink: 0,
      display: 'flex', flexDirection: 'column',
      overflowY: 'auto', background: theme.bgSecondary,
    }}>
      <div style={{ padding: '0 8px' }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          padding: '8px 8px 8px', letterSpacing: '0.08em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>collections</span>
          <button
            onClick={() => setShowNewCollection(true)}
            style={{
              background: 'none', border: 'none', color: theme.textFaint,
              cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1,
            }}
            title="Create collection"
          >+</button>
        </div>

        {showNewCollection && (
          <div style={{ padding: '2px 4px', marginBottom: 4 }}>
            <input
              autoFocus
              placeholder="Collection name..."
              value={newCollectionName}
              onChange={e => setNewCollectionName(e.target.value)}
              onKeyDown={async e => {
                if (e.key === 'Enter' && newCollectionName.trim()) {
                  await onCreateCollection({ name: newCollectionName.trim() });
                  setNewCollectionName('');
                  setShowNewCollection(false);
                }
                if (e.key === 'Escape') { setNewCollectionName(''); setShowNewCollection(false); }
              }}
              onBlur={() => { setNewCollectionName(''); setShowNewCollection(false); }}
              style={{
                width: '100%', padding: '6px 8px',
                background: theme.bgInput, border: `1px solid ${theme.accentBorder}`,
                borderRadius: 0, color: theme.text, fontSize: 12, outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
        )}

        {rootCollections.map(c => renderCollection(c))}
      </div>

      <div style={{ padding: '0 8px', marginTop: 12 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          padding: '8px 8px 8px', letterSpacing: '0.08em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>tags</span>
          <button
            onClick={async () => {
              const name = prompt('Tag name:');
              if (!name?.trim()) return;
              const colors = ['#FF0000', '#FFFF00', '#58585B', '#757575', '#707070', '#B3B3B3', '#1A1A1C', '#F5F5F5'];
              const color = colors[Math.floor(Math.random() * colors.length)];
              await onCreateTag({ name: name.trim(), color });
            }}
            style={{
              background: 'none', border: 'none', color: theme.textFaint,
              cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1,
            }}
            title="Create tag"
          >+</button>
        </div>
        {tags.map(t => (
          <button
            key={t.id}
            onClick={() => onNavigate('tag', t.name)}
            style={sidebarBtnStyle(activeTag === t.name)}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: t.color || theme.accent, flexShrink: 0,
              }} />
              {t.name}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, color: theme.textGhost, fontWeight: 600 }}>{t.font_count}</span>
              <span
                onClick={async e => {
                  e.stopPropagation();
                  if (confirm(`Delete tag "${t.name}"?`)) {
                    await onRemoveTag(t.id);
                    if (activeTag === t.name) onNavigate('allFonts');
                  }
                }}
                style={{
                  fontSize: 11, color: theme.textDisabled, cursor: 'pointer',
                  padding: '0 2px', lineHeight: 1,
                }}
                title="Delete tag"
              >✕</span>
            </span>
          </button>
        ))}
        {tags.length === 0 && (
          <div style={{ fontSize: 11, color: theme.textDisabled, padding: '4px 8px' }}>No tags yet</div>
        )}
      </div>

      <div style={{ flex: 1 }} />

      <div style={{
        padding: 16, margin: '0 12px',
        background: 'none',
        borderRadius: 0, borderTop: `2px solid ${theme.text}`,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>desktop agent</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ width: 6, height: 6, background: theme.accent, display: 'inline-block' }} />
          <span style={{ color: theme.text }}>connected</span>
        </div>
        <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 6, letterSpacing: '0.02em' }}>
          macOS · v1.0.2 · last sync 2m ago
        </div>
      </div>
    </aside>
  );
}
